import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  OnInit,
  HostListener,
  inject,
} from '@angular/core';

import { MediaAudioModel } from '@modules/media/models/media-audio.model';
import { MediaDocumentModel } from '@modules/media/models/media-document.model';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { MediaType } from '@modules/media/models/media.types';
import { LightboxService } from '@modules/shared/services/lightbox.service';
import { TrackingService, TRACKING_TYPES } from '@modules/shared/services/tracking.service';
import { Screen } from '@modules/shared/services/window.service';

import { AudioComponent } from '../../../media/audio/audio.component';
import { DocumentPreviewComponent } from '../../../media/document-preview/document-preview.component';
import { ImageComponent } from '../../../media/image/image.component';
import { PdfPreviewComponent } from '../../../media/pdf-preview/pdf-preview.component';
import { VideoComponent } from '../../../media/video/video.component';

type mediaListType = MediaImageModel | MediaVideoModel | MediaAudioModel | MediaDocumentModel;

@Component({
  selector: 'mb-lightbox-media-gallery',
  templateUrl: './lightbox-media-gallery.component.html',
  styleUrls: ['./lightbox-media-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    ImageComponent,
    PdfPreviewComponent,
    DocumentPreviewComponent,
    AudioComponent,
    VideoComponent,
  ],
})
export class LightboxMediaGalleryComponent implements OnInit {
  private lightboxService = inject(LightboxService);
  private trackingService = inject(TrackingService);
  private cdr = inject(ChangeDetectorRef);

  public mediaType = MediaType;

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key == 'ArrowRight') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    }
  }

  @Input() activeIndex = 0;
  @Input() mediaList: mediaListType[] = [];
  @Input() isPreMedia: boolean = false;

  public prev() {
    if (this.activeIndex === 0) {
      this.activeIndex = this.mediaList.length - 1;
    } else {
      this.activeIndex--;
    }
    this.updateAfterChange();
  }

  public next() {
    if (this.activeIndex === this.mediaList.length - 1) {
      this.activeIndex = 0;
    } else {
      this.activeIndex++;
    }
    this.updateAfterChange();
  }

  private touchStartX = 0;

  public onTouchStart(event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  public onTouchEnd(event: TouchEvent) {
    if (window.screen.width >= Screen.MD) {
      return;
    }
    const diff = this.touchStartX - event.changedTouches[0].screenX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? this.prev() : this.next();
    }
  }

  private updateAfterChange() {
    const metadata = this.mediaList[this.activeIndex].getLightboxMetadata();
    this.lightboxService.sidebarData$.next(metadata);
    this.lightboxService.activeResource = this.mediaList[this.activeIndex];
    this.lightboxService.activeMediaIndex = this.activeIndex;
    this.cdr.detectChanges();
    this.trackingService.trackEvent(this.mediaList[this.activeIndex], TRACKING_TYPES.VIEWED);
  }

  ngOnInit(): void {
    this.lightboxService.activeResource = this.mediaList[this.activeIndex];
  }
}
